import { Component, ContentChildren, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, TemplateRef } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Observable, Subject, takeUntil } from 'rxjs';

import { getRowActionItems, IRowActionGetter, IRowActionItem, IRowClassNamesGetter, ITableData, ITableRow, ITableSettings } from './interfaces';
import { IEventSettings, IField, SortDirection } from '../shared/interfaces';
import { AppLoggerService } from '../shared/app-logger.service';
import { InputFieldCreatorService } from '../shared/input-field-creator.service';
import { TableNoDataDirective } from './table.no-data.directive';

@Component({
    selector: 'aza-rws-table',
    templateUrl: 'table.component.html',
    standalone: false,
})
export class TableComponent<RowData extends {} = any> implements OnInit, OnDestroy {
    @Input({
        required: true,
    }) data$: Observable<ITableData<RowData>>;

    private _rowActionGetter: IRowActionGetter<RowData>;
    public get rowActionGetter(): IRowActionGetter<RowData> {
        return this._rowActionGetter;
    }
    @Input()
    public set rowActionGetter(value: IRowActionGetter<RowData>) {
        this._rowActionGetter = value;
        this.processRowActionOptionsAvailable(value);
    }

    @Input() rowClassNamesGetter?: IRowClassNamesGetter<RowData>;

    @Input() eventSettings: IEventSettings;
    // @Input() showDateConversionToggle = true;

    @Input() extraRowDivTemplate?: TemplateRef<{ row: ITableRow<RowData>, isCardView: boolean }>;

    @Output() pageRequested = new EventEmitter<ITableSettings>();
    @Output() sortRequested = new EventEmitter<ITableSettings>();
    @Output() dateConversionRequested = new EventEmitter<ITableSettings>();

    @ContentChildren(TableNoDataDirective) noDataElements!: QueryList<TableNoDataDirective>;

    rows: ITableRow<RowData>[] = [];
    columns: IField[] = [];
    pagingData: IPagingData = { itemFrom: 0, itemTo: 0, totalItems: 0 };
    form: FormGroup<IFormControls>;
    tableSettings: ITableSettings;
    lastData: ITableData<RowData>;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly fb: FormBuilder,
        private logSvc: AppLoggerService,
        private inputFieldSvc: InputFieldCreatorService,
    ) {
    }

    ngOnInit(): void {
        this.initForm();
        if (this.data$) {
            this.data$.pipe(takeUntil(this.destroy$))
                .subscribe(data => this.processDataAvailable(data));
        }
    }

    onFirstPageRequested(): void {
        if (this.tableSettings.page.offset === 0) {
            return;
        }
        this.tableSettings.page.offset = 0;
        this.pageRequested.emit(this.tableSettings);
    }

    onNextPageRequested(): void {
        const pageSize = this.tableSettings.page.limit;
        let newOffset = this.pagingData.itemFrom - 1 + pageSize;
        if (newOffset === this.tableSettings.page.offset || newOffset > this.pagingData.totalItems - 1) {
            return;
        }
        this.tableSettings.page.offset = newOffset;
        this.pageRequested.emit(this.tableSettings);
    }

    onPrevPageRequested(): void {
        const pageSize = this.tableSettings.page.limit;
        let newOffset = Math.floor((this.pagingData.itemFrom - 1 - pageSize) / pageSize) * pageSize;
        if (newOffset < 0) {
            newOffset = 0;
        }
        if (newOffset === this.tableSettings.page.offset) {
            return;
        }
        this.tableSettings.page.offset = newOffset;
        this.pageRequested.emit(this.tableSettings);
    }

    onLastPageRequested(): void {
        const pageSize = this.tableSettings.page.limit;
        const newOffset = Math.floor(this.pagingData.totalItems / pageSize) * pageSize;
        if (newOffset === this.tableSettings.page.offset) {
            return;
        }
        this.tableSettings.page.offset = newOffset;
        this.pageRequested.emit(this.tableSettings);
    }

    onRowActionSelected(action: IRowActionItem<RowData>, row: ITableRow<RowData>, ev: Event): void {
        ev.preventDefault();
        action.actionFn(row.object);
    }

    onRowCardSelected(row: ITableRow<RowData>, ev: Event): void {
        ev.preventDefault();
        const actionObject = this.rowActionGetter?.(row.object);
        actionObject?.viewFn?.(row.object);
    }

    onColumnSorted(column: IField): void {
        if (column.name === this.tableSettings.sort.fieldName) {
            const newSortDirection = this.tableSettings.sort.sortDirection === SortDirection.asc ? SortDirection.desc : SortDirection.asc;
            this.tableSettings.sort = {
                fieldName: column.name,
                sortDirection: newSortDirection
            };
        } else {
            this.tableSettings.sort = {
                fieldName: column.name,
                sortDirection: SortDirection.asc
            };
        }

        this.sortRequested.emit(this.tableSettings);
    }

    private initForm(): void {
        this.form = this.fb.group<IFormControls>({
            itemsPerPage: new FormControl<number>(5)
        });
        this.form.controls.itemsPerPage.valueChanges.pipe(
            takeUntil(this.destroy$)
        ).subscribe(value => this.processItemsPerPageChanged(value));
    }

    private processItemsPerPageChanged(value: number): void {
        this.tableSettings.page.limit = value;
        this.pageRequested.emit(this.tableSettings);
    }

    private processDataAvailable(data: ITableData<RowData>): void {
        this.lastData = data;
        this.tableSettings ||= {
            page: { limit: data.pagedResponse.limit, offset: data.pagedResponse.offset },
            sort: { fieldName: data.sortData?.fieldName, sortDirection: data.sortData?.sortDirection }
        };
        this.columns = data.columns || this.columns || [];
        this.rows = this.createRows(data, this.columns);
        this.pagingData = {
            itemFrom: data.pagedResponse.offset + 1,
            itemTo: data.pagedResponse.offset + data.rowObjects.length,
            totalItems: data.pagedResponse.object_count,
        };
        // if (data.sortData) {
        //     this.tableSettings.sort = {
        //         fieldName: data.sortData.fieldName,
        //         sortDirection: data.sortData.sortDirection
        //     };
        // }
    }

    private processRowActionOptionsAvailable(actionGetter: IRowActionGetter<RowData>): void {
        if (!actionGetter) {
            return;
        }
        for (let i = 0; i < this.rows.length; i++) {
            const rowItem = this.rows[i];
            rowItem.actionItems = getRowActionItems(rowItem.object, actionGetter);
        }
    }

    private createRows(data: ITableData<RowData>, columns: IField[]): ITableRow<RowData>[] {
        const rows: ITableRow<RowData>[] = [];
        for (let i = 0; i < data.rowObjects.length; i++) {
            const dataRowObject = data.rowObjects[i];
            const actionItems = getRowActionItems(dataRowObject, this.rowActionGetter) || [];
            const row: ITableRow<RowData> = { cells: [], object: dataRowObject, actionItems };
            for (const column of columns) {
                row.cells.push({
                    text: this.getCellText(column, dataRowObject),
                    cssClass: '',
                    field: column,
                    value: this.getCellValue(dataRowObject, column)
                });
            }
            rows.push(row);
        }
        return rows;
    }

    private getCellText(column: IField, obj: RowData): string {
        let result = '';
        const objValue = this.getCellValue(obj, column);
        // TODO: The table would need more information in order to auto-format dates and times
        result = this.inputFieldSvc.getTranslationFromField(column, objValue) ?? objValue;
        return result;

        // if (column.schema?.format === 'datetime') {
        //     result = this.momentSvc.formatDateTime(objValue, obj.timezone || column.timezone || 'UTC', '', this.eventSettings?.datetime_format);
        // } else {
        //     result = objValue;
        // }
        // return result;
    }

    private getCellValue(obj: RowData, column: IField) {
        return obj[column.name] ?? obj[column.id];
    }

    replaceSpace(text: string, replacement: string) {
        return text.replace(/ /g, replacement);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}

interface IPagingData {
    itemFrom: number;
    itemTo: number;
    totalItems: number;
}

interface IFormControls {
    itemsPerPage: FormControl<number>
}
